<template>
  <div class="test-roo-renderer">
    <div class="test-header">
      <h2>Roo Message Renderer 测试页面</h2>
      <div class="test-controls">
        <el-select v-model="currentTest" placeholder="选择测试用例">
          <el-option
            v-for="(test, index) in testCases"
            :key="index"
            :label="test.name"
            :value="index"
          />
        </el-select>
        <el-button @click="runTest" type="primary">渲染测试</el-button>
      </div>
    </div>

    <div class="test-content">
      <div class="test-panel">
        <h3>Markdown 源码</h3>
        <el-input
          type="textarea"
          v-model="markdown"
          :rows="10"
          placeholder="输入 Markdown 内容"
        />
      </div>

      <div class="test-panel">
        <h3>渲染结果</h3>
        <div class="render-container">
          <roo-message-wrapper
            v-if="markdown"
            :markdown="markdown"
            class-name="test-render-output"
          />
          <div v-else class="placeholder">请选择测试用例或输入 Markdown</div>
        </div>
      </div>
    </div>

    <div class="test-info">
      <h3>当前测试: {{ testCases[currentTest]?.name }}</h3>
      <p>{{ testCases[currentTest]?.description }}</p>
    </div>
  </div>
</template>

<script>
import RooMessageWrapper from '../components/message/RooMessageWrapper.vue'

export default {
  name: 'TestRooRenderer',
  components: {
    RooMessageWrapper,
  },

  data() {
    return {
      currentTest: 0,
      markdown: '',
      testCases: [
        {
          name: '基础 Markdown',
          description: '测试标题、段落、粗体、斜体等基础语法',
          content: `# 一级标题
## 二级标题
### 三级标题

这是一个**粗体**文本和一个*斜体*文本。

这是一个[链接](https://example.com)。

- 无序列表项 1
- 无序列表项 2
- 无序列表项 3

1. 有序列表项 1
2. 有序列表项 2
3. 有序列表项 3`,
        },
        {
          name: '代码高亮',
          description: '测试行内代码和代码块语法高亮',
          content: `这是一个行内代码示例：\`const x = 10\`

下面是一个 JavaScript 代码块：

\`\`\`javascript
function fibonacci(n) {
  if (n <= 1) return n
  return fibonacci(n - 1) + fibonacci(n - 2)
}

console.log(fibonacci(10))
\`\`\`

Python 代码示例：

\`\`\`python
def hello_world():
    print("Hello, World!")
    for i in range(5):
        print(f"Count: {i}")

hello_world()
\`\`\``,
        },
        {
          name: 'Mermaid 图表',
          description: '测试 Mermaid 流程图、时序图等',
          content: `下面是一个流程图：

\`\`\`mermaid
graph TD
    A[开始] --> B{是否登录?}
    B -->|是| C[显示主页]
    B -->|否| D[跳转登录页]
    C --> E[结束]
    D --> E
\`\`\`

时序图示例：

\`\`\`mermaid
sequenceDiagram
    participant 用户
    participant 前端
    participant 后端
    participant 数据库

    用户->>前端: 发送请求
    前端->>后端: API 调用
    后端->>数据库: 查询数据
    数据库-->>后端: 返回数据
    后端-->>前端: 响应结果
    前端-->>用户: 显示内容
\`\`\``,
        },
        {
          name: '数学公式',
          description: '测试 KaTeX 数学公式渲染',
          content: `行内公式示例：质能方程 $E = mc^2$

块级公式示例：

$$
\\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}
$$

复杂公式：

$$
\\begin{aligned}
\\nabla \\times \\vec{\\mathbf{B}} -\\, \\frac1c\\, \\frac{\\partial\\vec{\\mathbf{E}}}{\\partial t} &= \\frac{4\\pi}{c}\\vec{\\mathbf{j}} \\\\
\\nabla \\cdot \\vec{\\mathbf{E}} &= 4 \\pi \\rho \\\\
\\nabla \\times \\vec{\\mathbf{E}}\\, +\\, \\frac1c\\, \\frac{\\partial\\vec{\\mathbf{B}}}{\\partial t} &= \\vec{\\mathbf{0}} \\\\
\\nabla \\cdot \\vec{\\mathbf{B}} &= 0
\\end{aligned}
$$`,
        },
        {
          name: '综合测试',
          description: '混合使用所有功能',
          content: `# Roo Message Renderer 综合测试

## 1. 代码示例

下面是一个 TypeScript 函数：

\`\`\`typescript
interface User {
  id: number
  name: string
  email: string
}

async function fetchUser(userId: number): Promise<User> {
  const response = await fetch(\`/api/users/\${userId}\`)
  return response.json()
}
\`\`\`

## 2. 数学公式

斐波那契数列的通项公式：

$$
F_n = \\frac{1}{\\sqrt{5}}\\left[\\left(\\frac{1+\\sqrt{5}}{2}\\right)^n - \\left(\\frac{1-\\sqrt{5}}{2}\\right)^n\\right]
$$

## 3. 流程图

\`\`\`mermaid
graph LR
    A[输入数据] --> B[数据处理]
    B --> C{验证通过?}
    C -->|是| D[保存到数据库]
    C -->|否| E[返回错误]
    D --> F[发送通知]
    E --> F
    F --> G[结束]
\`\`\`

## 4. 列表和引用

> 这是一段重要的引用文字。
>
> 可以包含多个段落。

任务列表：

- [x] 完成 UMD 库构建
- [x] 创建 Vue 包装组件
- [x] 集成到 LLMStreamMessage
- [ ] 完成测试验证`,
        },
        {
          name: '表格测试',
          description: '测试 Markdown 表格渲染',
          content: `# 表格示例

| 姓名 | 年龄 | 职位 |
|------|------|------|
| 张三 | 28 | 工程师 |
| 李四 | 32 | 设计师 |
| 王五 | 25 | 产品经理 |

对齐方式示例：

| 左对齐 | 居中对齐 | 右对齐 |
|:-------|:--------:|-------:|
| 内容1  | 内容2    | 内容3  |
| 很长的内容 | 中等 | 短 |`,
        },
      ],
    }
  },

  mounted() {
    this.runTest()
  },

  methods: {
    runTest() {
      if (this.testCases[this.currentTest]) {
        this.markdown = this.testCases[this.currentTest].content
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.test-roo-renderer {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;

  .test-header {
    margin-bottom: 20px;

    h2 {
      margin: 0 0 16px 0;
      color: #303133;
    }

    .test-controls {
      display: flex;
      gap: 12px;
      align-items: center;

      .el-select {
        width: 300px;
      }
    }
  }

  .test-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;

    .test-panel {
      h3 {
        margin: 0 0 12px 0;
        font-size: 16px;
        color: #303133;
      }

      .render-container {
        min-height: 400px;
        max-height: 600px;
        overflow-y: auto;
        padding: 16px;
        background: #f5f7fa;
        border-radius: 8px;
        border: 1px solid #dcdfe6;

        .placeholder {
          color: #909399;
          text-align: center;
          padding: 100px 20px;
        }

        ::v-deep .test-render-output {
          background: white;
          padding: 16px;
          border-radius: 4px;
        }
      }
    }
  }

  .test-info {
    padding: 16px;
    background: #ecf5ff;
    border-left: 4px solid #409eff;
    border-radius: 4px;

    h3 {
      margin: 0 0 8px 0;
      font-size: 14px;
      color: #409eff;
    }

    p {
      margin: 0;
      font-size: 13px;
      color: #606266;
    }
  }
}
</style>
